import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, Mail, Phone, MapPin, Calendar, Briefcase, Building2, CreditCard, Cake, Edit2, Camera, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin, isHR, getToken } from '@/lib/auth';
import api from '@/lib/api';

const STANDARD_KEYS = new Set([
  'id', 'employee_code', 'name', 'designation', 'department', 'email', 'phone',
  'status', 'employment_type', 'date_of_joining', 'date_of_birth', 'location',
  'personal_email', 'alternate_phone', 'aadhar_number', 'emergency_contact_relationship',
  'office_accessories', 'reporting_manager', 'reporting_manager_id', 'org_role',
  'created_at', 'updated_at', 'photo_path', 'photo_updated_at'
]);

function canEditEmployee() {
  return isAdmin() || isHR();
}

export default function EmployeeProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const canEdit = canEditEmployee();
  const fileInputRef = React.useRef(null);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/api/employees/${id}`);
      setData(d);
      loadPhoto();
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const loadPhoto = async () => {
    try {
      const res = await api.get(`/api/employees/${id}/photo`, { responseType: 'blob' });
      setPhotoUrl(URL.createObjectURL(res.data));
    } catch { setPhotoUrl(null); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG or WebP images allowed'); return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large (max 2MB)'); return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/api/employees/${id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo uploaded');
      loadPhoto();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleEdit = async () => {
    const payload = { ...editForm };
    // Add custom fields to payload
    customFields.forEach(cf => {
      if (cf.key.trim()) {
        payload[cf.key.trim()] = cf.value;
      }
    });
    // Remove empty values
    Object.keys(payload).forEach(k => {
      if (payload[k] === '' || payload[k] === undefined) delete payload[k];
    });
    try {
      await api.put(`/api/employees/${id}`, payload);
      toast.success('Employee updated');
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openEdit = () => {
    const emp = data?.employee;
    setEditForm({
      name: emp?.name || '',
      designation: emp?.designation || '',
      department: emp?.department || '',
      email: emp?.email || '',
      phone: emp?.phone || '',
      date_of_birth: emp?.date_of_birth || '',
      location: emp?.location || '',
      date_of_joining: emp?.date_of_joining || '',
      employment_type: emp?.employment_type || '',
      personal_email: emp?.personal_email || '',
      alternate_phone: emp?.alternate_phone || '',
      aadhar_number: emp?.aadhar_number || '',
      emergency_contact_relationship: emp?.emergency_contact_relationship || '',
      office_accessories: emp?.office_accessories || '',
      reporting_manager: emp?.reporting_manager || '',
    });
    // Extract custom fields (non-standard keys)
    const cf = [];
    if (emp) {
      Object.entries(emp).forEach(([k, v]) => {
        if (!STANDARD_KEYS.has(k) && v !== null && v !== undefined) {
          cf.push({ key: k, value: String(v) });
        }
      });
    }
    setCustomFields(cf);
    setNewFieldKey('');
    setNewFieldValue('');
    setEditOpen(true);
  };

  const addCustomField = () => {
    if (!newFieldKey.trim()) { toast.error('Enter a field name'); return; }
    if (STANDARD_KEYS.has(newFieldKey.trim().toLowerCase())) {
      toast.error('This is a standard field — edit it above'); return;
    }
    setCustomFields([...customFields, { key: newFieldKey.trim(), value: newFieldValue }]);
    setNewFieldKey('');
    setNewFieldValue('');
  };

  const removeCustomField = (idx) => {
    setCustomFields(customFields.filter((_, i) => i !== idx));
  };

  const updateCustomField = (idx, field, value) => {
    const updated = [...customFields];
    updated[idx] = { ...updated[idx], [field]: value };
    setCustomFields(updated);
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-48 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>;
  if (!data) return <p className="text-slate-500">Employee not found</p>;

  const { employee: emp, salary_structure: sal, leave_balance: lb } = data;
  const initials = emp.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  // Extract custom fields for display
  const displayCustomFields = Object.entries(emp).filter(([k]) => !STANDARD_KEYS.has(k));

  return (
    <div className="space-y-6" data-testid="employee-profile-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/employees')} className="text-slate-600"><ArrowLeft className="w-4 h-4 mr-1" />Back to Directory</Button>

      {/* Header Card */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <div className="relative group">
              <Avatar className="h-24 w-24 border-2 border-slate-200">
                {photoUrl ? <img src={photoUrl} alt={emp.name} className="h-full w-full object-cover rounded-full" /> : <AvatarFallback className="bg-[#0D2850] text-white text-2xl font-bold">{initials}</AvatarFallback>}
              </Avatar>
              {canEdit && (
                <button onClick={() => fileInputRef.current?.click()} className="absolute -bottom-1 -right-1 bg-[#0D2850] text-white rounded-full p-1.5 shadow-md hover:bg-[#0B2246] transition-colors" data-testid="upload-photo-btn" disabled={uploading}>
                  <Camera className="w-3.5 h-3.5" />
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhotoUpload} data-testid="photo-file-input" />
              {!photoUrl && <p className="text-[10px] text-slate-400 text-center mt-1">No photo</p>}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-semibold text-[#0B1B33]">{emp.name}</h1>
                <Badge className={emp.status === 'active' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}>{emp.status}</Badge>
              </div>
              <p className="text-sm text-slate-500">{emp.designation} &bull; {emp.department}</p>
              <p className="text-xs text-slate-400 mt-1">{emp.employee_code}</p>
              <div className="flex flex-wrap gap-4 mt-3">
                {emp.email && <span className="flex items-center gap-1.5 text-xs text-slate-500"><Mail className="w-3.5 h-3.5" />{emp.email}</span>}
                {emp.phone && <span className="flex items-center gap-1.5 text-xs text-slate-500"><Phone className="w-3.5 h-3.5" />{emp.phone}</span>}
                {emp.date_of_joining && <span className="flex items-center gap-1.5 text-xs text-slate-500"><Calendar className="w-3.5 h-3.5" />Joined: {emp.date_of_joining}</span>}
                {emp.date_of_birth && <span className="flex items-center gap-1.5 text-xs text-slate-500"><Cake className="w-3.5 h-3.5" />DOB: {emp.date_of_birth}</span>}
                {emp.location && <span className="flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="w-3.5 h-3.5" />{emp.location}</span>}
              </div>
            </div>
            {canEdit && (
              <Button variant="outline" size="sm" onClick={openEdit} className="gap-1" data-testid="edit-employee-btn">
                <Edit2 className="w-3 h-3" /> Edit
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="salary">Salary</TabsTrigger>
          <TabsTrigger value="leave">Leave</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Personal Information</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <InfoRow icon={Briefcase} label="Designation" value={emp.designation} />
                <InfoRow icon={Building2} label="Department" value={emp.department} />
                <InfoRow icon={Calendar} label="Date of Joining" value={emp.date_of_joining || 'N/A'} />
                <InfoRow icon={Cake} label="Date of Birth" value={emp.date_of_birth || 'Not set'} />
                <InfoRow icon={MapPin} label="Location" value={emp.location || 'Not set'} />
                <InfoRow icon={Mail} label="Personal Email" value={emp.personal_email || 'N/A'} />
                <InfoRow icon={Phone} label="Alternate Phone" value={emp.alternate_phone || 'N/A'} />
                <InfoRow icon={CreditCard} label="Aadhar" value={emp.aadhar_number ? `****${emp.aadhar_number.slice(-4)}` : 'N/A'} />
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Employment Details</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <InfoRow label="Employee Code" value={emp.employee_code} />
                <InfoRow label="Employment Type" value={emp.employment_type} />
                <InfoRow label="Reporting Manager" value={emp.reporting_manager || 'N/A'} />
                <InfoRow label="Emergency Contact" value={emp.emergency_contact_relationship || 'N/A'} />
                <InfoRow label="Office Accessories" value={emp.office_accessories || 'None assigned'} />
              </CardContent>
            </Card>
          </div>

          {/* Custom Fields */}
          {displayCustomFields.length > 0 && (
            <Card className="border-slate-200 mt-6">
              <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Additional Information</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {displayCustomFields.map(([key, value]) => (
                  <InfoRow key={key} label={key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} value={String(value)} />
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="salary" className="mt-4">
          {sal ? (
            <Card className="border-slate-200">
              <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Salary Structure</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <SalaryItem label="Gross Rate" value={sal.gross_rate} />
                  <SalaryItem label="Basic + DA" value={sal.basic_da_rate} />
                  <SalaryItem label="DA" value={sal.da} />
                  <SalaryItem label="HRA" value={sal.hra} />
                  <SalaryItem label="Conveyance" value={sal.conveyance} />
                  <SalaryItem label="Other Allowance" value={sal.other_allowance} />
                  <SalaryItem label="Medical" value={sal.medical} />
                  <SalaryItem label="KRA Amount" value={sal.kra_amount} />
                  <SalaryItem label="Last CTC" value={sal.last_ctc} />
                  <SalaryItem label="Current CTC" value={sal.current_ctc} />
                </div>
                <div className="flex gap-3 mt-4">
                  <Badge className={sal.pf_applicable ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-100'}>PF: {sal.pf_applicable ? 'Yes' : 'No'}</Badge>
                  <Badge className={sal.esic_applicable ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-100'}>ESIC: {sal.esic_applicable ? 'Yes' : 'No'}</Badge>
                </div>
              </CardContent>
            </Card>
          ) : <p className="text-slate-400 text-sm">No salary structure configured</p>}
        </TabsContent>

        <TabsContent value="leave" className="mt-4">
          {lb ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <LeaveCard title="Casual Leave" data={lb.casual_leave} />
              <LeaveCard title="Earned Leave" data={lb.earned_leave} />
              <LeaveCard title="Sick Leave" data={lb.sick_leave} />
            </div>
          ) : <p className="text-slate-400 text-sm">No leave balance data</p>}
        </TabsContent>
      </Tabs>

      {/* Edit Employee Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh]">
          <DialogHeader><DialogTitle>Edit Employee</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Basic Info</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Name</Label><Input value={editForm.name || ''} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="edit-name-input" /></div>
                <div><Label className="text-xs">Designation</Label><Input value={editForm.designation || ''} onChange={e => setEditForm({...editForm, designation: e.target.value})} data-testid="edit-designation-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Department</Label><Input value={editForm.department || ''} onChange={e => setEditForm({...editForm, department: e.target.value})} data-testid="edit-department-input" /></div>
                <div><Label className="text-xs">Employment Type</Label><Input value={editForm.employment_type || ''} onChange={e => setEditForm({...editForm, employment_type: e.target.value})} data-testid="edit-employment-type-input" /></div>
              </div>

              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider pt-2">Contact</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Email</Label><Input value={editForm.email || ''} onChange={e => setEditForm({...editForm, email: e.target.value})} data-testid="edit-email-input" /></div>
                <div><Label className="text-xs">Phone</Label><Input value={editForm.phone || ''} onChange={e => setEditForm({...editForm, phone: e.target.value})} data-testid="edit-phone-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Personal Email</Label><Input value={editForm.personal_email || ''} onChange={e => setEditForm({...editForm, personal_email: e.target.value})} data-testid="edit-personal-email-input" /></div>
                <div><Label className="text-xs">Alternate Phone</Label><Input value={editForm.alternate_phone || ''} onChange={e => setEditForm({...editForm, alternate_phone: e.target.value})} data-testid="edit-alternate-phone-input" /></div>
              </div>

              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider pt-2">Personal</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Date of Birth</Label><Input type="date" value={editForm.date_of_birth || ''} onChange={e => setEditForm({...editForm, date_of_birth: e.target.value})} data-testid="edit-dob-input" /></div>
                <div><Label className="text-xs">Date of Joining</Label><Input type="date" value={editForm.date_of_joining || ''} onChange={e => setEditForm({...editForm, date_of_joining: e.target.value})} data-testid="edit-doj-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Location</Label><Input value={editForm.location || ''} onChange={e => setEditForm({...editForm, location: e.target.value})} data-testid="edit-location-input" /></div>
                <div><Label className="text-xs">Aadhar Number</Label><Input value={editForm.aadhar_number || ''} onChange={e => setEditForm({...editForm, aadhar_number: e.target.value})} data-testid="edit-aadhar-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Emergency Contact</Label><Input value={editForm.emergency_contact_relationship || ''} onChange={e => setEditForm({...editForm, emergency_contact_relationship: e.target.value})} data-testid="edit-emergency-input" /></div>
                <div><Label className="text-xs">Reporting Manager</Label><Input value={editForm.reporting_manager || ''} onChange={e => setEditForm({...editForm, reporting_manager: e.target.value})} data-testid="edit-reporting-manager-input" /></div>
              </div>
              <div>
                <Label className="text-xs">Office Accessories</Label>
                <Input value={editForm.office_accessories || ''} onChange={e => setEditForm({...editForm, office_accessories: e.target.value})} data-testid="edit-accessories-input" />
              </div>

              {/* Custom Fields Section */}
              <div className="border-t pt-4 mt-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Custom Fields</p>
                {customFields.map((cf, idx) => (
                  <div key={idx} className="flex gap-2 items-end mb-2" data-testid={`custom-field-${idx}`}>
                    <div className="flex-1">
                      <Label className="text-[10px] text-slate-400">Field Name</Label>
                      <Input
                        value={cf.key}
                        onChange={e => updateCustomField(idx, 'key', e.target.value)}
                        placeholder="e.g. blood_group"
                        className="h-8 text-sm"
                        data-testid={`custom-field-key-${idx}`}
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-[10px] text-slate-400">Value</Label>
                      <Input
                        value={cf.value}
                        onChange={e => updateCustomField(idx, 'value', e.target.value)}
                        placeholder="e.g. O+"
                        className="h-8 text-sm"
                        data-testid={`custom-field-value-${idx}`}
                      />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeCustomField(idx)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700" data-testid={`remove-custom-field-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2 items-end mt-2">
                  <div className="flex-1">
                    <Input
                      value={newFieldKey}
                      onChange={e => setNewFieldKey(e.target.value)}
                      placeholder="New field name"
                      className="h-8 text-sm"
                      data-testid="new-custom-field-key"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      value={newFieldValue}
                      onChange={e => setNewFieldValue(e.target.value)}
                      placeholder="Value"
                      className="h-8 text-sm"
                      data-testid="new-custom-field-value"
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={addCustomField} className="h-8 gap-1" data-testid="add-custom-field-btn">
                    <Plus className="w-3.5 h-3.5" /> Add
                  </Button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Add any extra fields like PAN, blood group, bank details, etc.</p>
              </div>

              <Button onClick={handleEdit} className="w-full bg-[#0D2850] hover:bg-[#0B2246] mt-4" data-testid="save-employee-btn">Save Changes</Button>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-slate-50">
      <span className="text-sm text-slate-500 flex items-center gap-2">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</span>
      <span className="text-sm font-medium text-[#0B1B33] text-right max-w-[200px] truncate">{value}</span>
    </div>
  );
}

function SalaryItem({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-lg font-semibold text-[#0B1B33] font-tabular">&#8377;{(value || 0).toLocaleString('en-IN')}</p>
    </div>
  );
}

function LeaveCard({ title, data }) {
  const pct = data.entitled > 0 ? (data.balance / data.entitled) * 100 : 0;
  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{title}</p>
        <p className="text-3xl font-semibold text-[#0B1B33] font-tabular">{data.balance}</p>
        <p className="text-xs text-slate-400 mt-0.5">of {data.entitled} days</p>
        <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#0D2850] rounded-full" style={{ width: `${pct}%` }} />
        </div>
      </CardContent>
    </Card>
  );
}
